<template>
  <div>
    <transition
      mode="out-in"
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      leave-active-class="transition duration-200 ease-in"
      leave-to-class="opacity-0 translate-y-2"
    >
    <!-- Main Hydration Status Bar -->
    <status-bar
      v-if="
        (loadingPatents || question.patentsStatus?.isLoading) && !loadedPatents
      "
      key="loading"
      :total-step-count="totalStepCount"
      :current-step-number="currentStepNumber"
      :status="status || question.patentsStatus?.message"
      :began-at="question.patentsStatus?.beganAt"
      theme="purple"
      @cancel="$emit('cancel-loading', 'patents')"
    />
    <div
      v-else-if="!loadedPatents"
      key="load-action"
      class="text-center w-full py-8"
    >
      <!-- Reusable Decoupled Error Card -->
      <research-compass-error-card
        v-if="isFailed"
        title="Patent Hydration Failed"
        description="An unexpected error occurred while compiling intellectual property sources."
        button-text="Retry Patent Search"
        button-class="bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600"
        @retry="getPatents()"
      />

      <!-- Standard Load Patents Action -->
      <template v-else>
        <button
          class="bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white px-6 py-2.5 uppercase text-sm font-bold rounded-xl transition-all shadow-lg flex items-center mx-auto gap-2"
          @click="getPatents()"
        >
          <i class="fas fa-lightbulb"></i>
          Load Patents
        </button>
        <div
          class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
        >
          <i class="fa-regular fa-clock-three text-purple-500/70"></i>
          Usually takes about 10 minutes
        </div>
      </template>
    </div>
    <div v-else-if="loadedPatents" key="content">
      <div class="mb-8 min-h-[100px]">
        <!-- Explanation (Summary) View -->
        <div v-if="selectedSubTab === 'summary'">
          <div class="flex justify-end mb-2">
            <button
              v-if="
                !question.patents?.patents ||
                question.patents.patents.length === 0
              "
              class="text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-purple-600 transition-colors flex items-center gap-1.5"
              @click="getPatents()"
            >
              <i class="fas fa-arrows-rotate"></i>
              Retry Patent Search
            </button>
          </div>
          <research-compass-markdown-viewer
            :text="question.patents.summary"
            :patents="question.patents.patents"
            :question="question"
            ref-name="patentHolder"
            @scroll-to-citation="scrollToCitation"
          />
          <div class="flex justify-end mb-4 mt-2">
            <feedback-modal
              sentiment="POSITIVE"
              item-type="PATENT_SUMMARY"
              :item-id="question.hashedId"
              :ancillary-information-label="question.question"
              :research-compass-hashed-id="$route.params.researchCompassId"
            />
            <feedback-modal
              sentiment="NEGATIVE"
              item-type="PATENT_SUMMARY"
              :item-id="question.hashedId"
              :ancillary-information-label="question.question"
              :research-compass-hashed-id="$route.params.researchCompassId"
            />
          </div>
        </div>

        <!-- Comparison (Table) View -->
        <div v-if="selectedSubTab === 'table'">
          <div v-if="generatingTable" class="py-10 px-6">
            <status-bar
              :total-step-count="totalStepCount"
              :current-step-number="currentStepNumber"
              :status="status"
              theme="purple"
            />
          </div>
          <div
            v-else-if="!table"
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700"
          >
            <div
              v-if="
                !question.patents?.patents ||
                question.patents.patents.length === 0
              "
              class="text-center"
            >
              <i class="fas fa-info-circle text-gray-400 text-2xl mb-2"></i>
              <p class="font-bold text-gray-500 text-sm">
                No patents available to compare.
              </p>
            </div>
            <button
              v-else
              class="bg-white dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-purple-900/30 border border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-400 px-6 py-2.5 uppercase text-xs font-bold rounded-xl transition-all shadow-sm flex items-center mx-auto gap-2"
              @click="generateTable()"
            >
              <i class="fas fa-wand-magic-sparkles"></i>
              Generate Comparison Table
            </button>
            <div
              class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
            >
              <i class="fa-regular fa-clock-three text-purple-500/70"></i>
              Usually takes about a minute
            </div>
          </div>
          <div v-else class="overflow-x-auto pb-4 custom-scrollbar">
            <research-compass-markdown-viewer
              :text="table"
              :question="question"
              ref-name="patentHolder"
              :show-table-scroll-indicators="true"
              @scroll-to-citation="scrollToCitation"
            />
            <div class="flex justify-end mt-4">
              <feedback-modal
                sentiment="POSITIVE"
                item-type="PATENT_TABLE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
              <feedback-modal
                sentiment="NEGATIVE"
                item-type="PATENT_TABLE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
            </div>
          </div>
        </div>

        <!-- List View -->
        <div v-if="selectedSubTab === 'list'" class="w-full">
          <div
            v-if="
              !question.patents?.patents ||
              question.patents.patents.length === 0
            "
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700"
          >
            <div class="text-center mb-6">
              <i class="fas fa-info-circle text-gray-400 text-3xl mb-3"></i>
              <p class="font-bold text-gray-600 dark:text-gray-400 text-sm">
                No relevant patents were found for this query.
              </p>
              <p class="text-gray-500 dark:text-gray-500 text-xs mt-2">
                Click below to try the search again with broader terms.
              </p>
            </div>
            <button
              class="bg-white dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-purple-900/30 border border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-400 px-6 py-2.5 uppercase text-xs font-bold rounded-xl transition-all shadow-sm flex items-center mx-auto gap-2"
              @click="getPatents()"
            >
              <i class="fas fa-arrows-rotate"></i>
              Retry Patent Search
            </button>
          </div>
          <template v-else>
            <div
              v-for="(patent, index) in question.patents.patents"
              :id="`patent${index + 1}`"
              :key="patent.patentId"
              class="bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl border border-gray-300 dark:border-gray-800 shadow-xl rounded-2xl p-5 mb-4 transition-all duration-300 hover:shadow-2xl"
            >
              <div class="w-full flex items-start">
                <div
                  class="bg-purple-600 dark:bg-purple-500 text-white font-mono font-bold w-12 h-12 rounded-xl flex items-center justify-center shrink-0 mr-4 text-xl shadow-md p-0"
                >
                  {{ index + 1 }}
                </div>
                <div class="w-full">
                  <div class="w-full flex justify-between items-start mb-2">
                    <a
                      v-if="patent.patentId"
                      target="_blank"
                      :href="`/research-compass/patent/${sqidEncodeString(
                        patent.patentId,
                      )}`"
                      class="group"
                    >
                      <h4
                        class="text-lg font-headline font-bold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors flex items-center gap-2"
                      >
                        <span v-html="patent.title" />
                        <i
                          class="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                        ></i>
                      </h4>
                    </a>
                    <div class="flex items-start gap-1 mt-1">
                      <feedback-modal
                        sentiment="POSITIVE"
                        item-type="PATENT"
                        :item-id="question.hashedId"
                        :ancillary-information="patent.patentId"
                        :ancillary-information-label="patent.title"
                        :research-compass-hashed-id="
                          $route.params.researchCompassId
                        "
                      />
                      <feedback-modal
                        sentiment="NEGATIVE"
                        item-type="PATENT"
                        :item-id="question.hashedId"
                        :ancillary-information="patent.patentId"
                        :ancillary-information-label="patent.title"
                        :research-compass-hashed-id="
                          $route.params.researchCompassId
                        "
                      />
                    </div>
                  </div>
                  <div class="flex flex-wrap items-center gap-2 mb-3 mt-1">
                    <span
                      v-if="usedCitationIndices.indexOf(index + 1) !== -1"
                      class="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800/50 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider inline-flex items-center"
                    >
                      <i class="fas fa-check-circle mr-1"></i>
                      Cited in Answer
                    </span>
                    <span
                      v-if="patent.assignee"
                      class="bg-gray-100 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 px-2 py-0.5 rounded text-[10px] font-bold flex items-center shadow-sm"
                    >
                      <i class="fas fa-building text-gray-500 mr-1.5"></i>
                      {{ patent.assignee }}
                    </span>
                    <span
                      v-if="patent.patentId"
                      class="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50 px-2 py-0.5 rounded text-[10px] font-mono font-bold flex items-center shadow-sm"
                    >
                      <i class="fas fa-hashtag text-purple-500 mr-1.5"></i>
                      {{ patent.patentId }}
                    </span>
                    <div
                      v-if="patent.cpcCode"
                      class="inline-flex items-center rounded border border-purple-200 dark:border-purple-800/50 overflow-hidden shadow-sm"
                    >
                      <span
                        class="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2 py-0.5 text-[10px] flex gap-1 items-center font-bold"
                      >
                        <i class="fas fa-tags text-purple-500"></i>
                        CPC
                      </span>
                      <a
                        target="_blank"
                        :href="generateCpcUrl(patent.cpcCode)"
                        class="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-l border-purple-200 dark:border-purple-800/50 px-2 py-0.5 text-[10px] font-mono font-bold hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                      >
                        {{ patent.cpcCode }}
                      </a>
                    </div>
                  </div>
                  <div
                    v-if="patent.contribution || patent.contributionReason"
                    class="bg-purple-500/5 dark:bg-purple-400/5 border-l-4 border-purple-500/50 dark:border-purple-400/30 rounded-r-lg px-5 py-4 mt-6 mb-4 text-sm text-gray-700 dark:text-gray-300 italic shadow-sm"
                  >
                    <div
                      class="text-[10px] font-bold uppercase tracking-widest text-purple-600/70 dark:text-purple-400/70 mb-2 flex items-center"
                    >
                      <i class="fas fa-sparkles mr-2"></i>
                      Why it fits
                    </div>
                    <div
                      v-html="patent.contribution || patent.contributionReason"
                    />
                  </div>

                  <div
                    class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3"
                  >
                    <div
                      class="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2 flex items-center"
                    >
                      <i class="fas fa-info-circle mr-2"></i>
                      Abstract
                    </div>
                    <div v-html="patent.abstract" />
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    </transition>
  </div>
</template>

<script>
import { nextTick } from "vue";
import * as utils from "../../utils";
import StatusBar from "./StatusBar.vue";
import FeedbackModal from "./FeedbackModal.vue";
import ResearchCompassMarkdownViewer from "./ResearchCompassMarkdownViewer.vue";
import ResearchCompassErrorCard from "./ResearchCompassErrorCard.vue";

const { sqidEncodeString, resolveRcApiUrl } = utils;

export default {
  components: {
    StatusBar,
    FeedbackModal,
    ResearchCompassMarkdownViewer,
    ResearchCompassErrorCard,
  },
  props: {
    question: Object,
    loadedPatents: Boolean,
    loadingPatents: Boolean,
    selectedSubTab: String,
  },
  emits: [
    "start-loading",
    "finish-loading",
    "cancel-loading",
    "reload-research-compass",
    "change-sub-tab",
    "get-patents",
    "update:generating-table",
  ],
  data() {
    return {
      totalStepCount: 0,
      currentStepNumber: 0,
      status: "",
      researchCompassQuestionChannel: null,
      generatingTable: false,
    };
  },
  computed: {
    isFailed() {
      const status = this.question.patentsStatus;
      return !!(status?.hasError || status?.message === "Error");
    },
    webBaseUrl() {
      return import.meta.env.VITE_WEB_BASE_URL;
    },
    usedCitationIndices() {
      if (!this.question.patents?.summary) return [];
      const regex = /\[([0-9, ]*?)\]/g;
      let result = this.question.patents.summary;
      const matches = result.match(regex);
      if (!matches) return [];
      [...new Set(matches)].forEach((match) => {
        if (match.indexOf(",") > -1) {
          const parts = match
            .slice(1, -1)
            .split(",")
            .map((part) => part.trim());
          const formattedParts = parts.map((part) => `[${part}]`);
          result = result.replaceAll(match, formattedParts.join(" "));
        }
      });
      const newMatches = result.match(regex);
      return [...new Set(newMatches)].map((item) =>
        Number(item.replace("[", "").replace("]", "").replace(" ", "")),
      );
    },
    table() {
      const answers = this.question.answers || [];
      const table = answers
        .filter(
          (answer) =>
            (answer.answerType?.value || answer.answerType) === "PATENT_TABLE",
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return table.length > 0 ? table[0].content : null;
    },
    isPublic() {
      return Boolean(this.$route.params.viewKey);
    },
    activeAxios() {
      if (this.isPublic) {
        return this.$api.axiosPublic;
      }
      return this.$api.axios;
    },
  },
  watch: {
    generatingTable(newVal) {
      this.$emit("update:generating-table", newVal);
    },
    table(newVal) {
      if (newVal) {
        this.generatingTable = false;
      }
    },
  },
  mounted() {
    this.setUpChannel(this.question.hashedId);
  },
  methods: {
    sqidEncodeString(id) {
      return sqidEncodeString(id);
    },
    scrollToCitation(citationId) {
      this.$emit("change-sub-tab", "list");
      nextTick(() => {
        const el = document.getElementById(`patent${citationId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      });
    },
    setUpChannel(hashedId) {
      if (this.researchCompassQuestionChannel === null) {
        const channel = this.$ably.channels.get(
          `${
            import.meta.env.VITE_ENV_CONFIG_NAMESPACE || ""
          }researchcompassquestion${hashedId}patents`,
        );
        const self = this;
        channel.subscribe((message) => {
          let data;
          try {
            data =
              typeof message.data === "string"
                ? JSON.parse(message.data)
                : message.data;
          } catch (e) {
            console.error("Failed to parse websocket message", e);
            return;
          }

          if (!data) return;

          // Data from Headllamp is nested in metadata
          const statusData = data.metadata || data;

          if (statusData.status !== "Error") {
            if (statusData.status) self.status = statusData.status;
            if (statusData.substatus !== undefined)
              self.substatus = statusData.substatus;
            if (!self.loadedPatents) {
              self.$emit("start-loading", "patents");
            }
            if (statusData.totalStepCount)
              self.totalStepCount = statusData.totalStepCount;
            if (statusData.currentStepNumber)
              self.currentStepNumber = statusData.currentStepNumber;
            if (statusData.reload) {
              self.$emit("reload-research-compass");
            }
            if (statusData.finished) {
              if (!self.loadedPatents) {
                self.$emit("finish-loading", "patents");
              }
            }
          } else {
            // Handle error state
            self.generatingTable = false;
            if (statusData.reload) {
              self.$emit("reload-research-compass");
            }
            self.$emit("cancel-loading", "patents");
          }
        });
        this.researchCompassQuestionChannel = channel;
      }
    },
    getPatents() {
      this.status = "Initializing patent hydration...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      this.$emit("get-patents");
    },
    generateCpcUrl(cpcCode) {
      if (!cpcCode) return "";
      const firstLetter = cpcCode.charAt(0);
      const numberSegment = cpcCode.match(/[0-9]+/)[0];
      const secondLetter = cpcCode.charAt(cpcCode.indexOf(numberSegment) + 2);
      return `https://www.uspto.gov/web/patents/classification/cpc/html/cpc-${firstLetter}${numberSegment}${secondLetter}.html#${cpcCode}`;
    },
    generateTable() {
      this.generatingTable = true;
      this.status = "Initializing comparison...";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "generate-patent-table",
        this.$route.params,
      );

      this.activeAxios.post(url).catch(() => {
        this.generatingTable = false;
      });
    },
  },
};
</script>
